import React from 'react';

/**
 * Primary navigation. Dark on every page, including the light workspaces.
 *
 * Lien Machine owns the top of the sidebar; The May Firm signs the foot of
 * it. The two marks are never set on one line — two logos side by side read
 * as a partnership rather than as a firm and its tool.
 *
 * Icons are inlined rather than pulled from an icon package. The app has no
 * icon dependency and six paths are not a reason to add one; inlining also
 * means the nav draws on the first frame instead of after a font or script
 * arrives.
 */

const ICONS = {
  dashboard: <><path d="M3 10.2 12 3l9 7.2V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z" /></>,
  projects: <><path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /></>,
  negotiations: <><path d="m12 3 9 4.2-9 4.2-9-4.2Z" /><path d="m3 12 9 4.2 9-4.2" /><path d="m3 16.5 9 4.2 9-4.2" /></>,
  providers: <><circle cx="12" cy="8.5" r="3.6" /><path d="M5 20.5a7 7 0 0 1 14 0" /></>,
  closeout: <><circle cx="12" cy="12" r="9" /><path d="m8.4 12.2 2.5 2.5 4.7-4.9" /></>,
  reconciliation: <><path d="M5 20V14" /><path d="M12 20V8.5" /><path d="M19 20V4.5" /></>,
};

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  // "Cases" is what the team calls them. Filevine project ids and phase
  // values are untouched behind the label.
  { id: 'projects', label: 'Cases', count: 'projectCount', of: 'cases in scope' },
  { id: 'negotiations', label: 'Negotiations', count: 'negotiationsNeedReview', of: 'active negotiations' },
  { id: 'providers', label: 'Providers' },
  { id: 'closeout', label: 'Closeout', count: 'closeoutCount', of: 'packets awaiting review' },
  { id: 'reconciliation', label: 'Reconciliation' },
];

const NavIcon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {ICONS[name]}
  </svg>
);

const Sidebar = ({
  activeTab,
  setActiveTab,
  projectCount = 0,
  negotiationsNeedReview = 0,
  closeoutCount = 0,
}) => {
  const counts = { projectCount, negotiationsNeedReview, closeoutCount };

  return (
    <aside className="lm-sidebar">
      {/* Supplied artwork, placed at its own aspect. Its dark ground is part
          of the file, which is why it is an <img> and not a masked shape. */}
      <img className="lm-logo" src="/brand/lm-logo.png" alt="Lien Machine" width="482" height="186" />

      <nav className="lm-nav" aria-label="Primary">
        {TABS.map(({ id, label, count, of }) => {
          const n = count ? counts[count] : 0;
          return (
            <button key={id} type="button"
                    aria-current={activeTab === id ? 'page' : undefined}
                    onClick={() => setActiveTab(id)}>
              <NavIcon name={id} />
              <span>{label}</span>
              {n > 0 && (
                <span className="lm-count">
                  {n}
                  {/* A bare "4" beside Closeout tells a screen reader
                      nothing about what four of anything it is. */}
                  <span className="lm-sr">{` ${of}`}</span>
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="lm-brand">
        <small>A product of</small>
        {/* The full colour logo as supplied — circular M, wordmark and the
            Injury Lawyers tagline — centred under the line above it. */}
        <img className="lm-firm-logo" src="/brand/mayfirm-full-color.png"
             alt="The May Firm Injury Lawyers" width="528" height="250" />
      </div>
    </aside>
  );
};

export default Sidebar;
